import { Link, Outlet, createRootRoute, useRouterState } from '@tanstack/react-router';

export const Route = createRootRoute({
  component: RootComponent,
});

function RootComponent() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  /**
   * The administration screens paint their OWN window (BUILD-851).
   *
   * They used to borrow this one - a 160 px logo above a centred column, widened
   * to `max-w-6xl` because the journal carries twelve columns and a 620 px panel
   * beside them (transcript admin §6). The target shell of the maquette is
   * another chrome entirely: a sticky full-bleed bar, brand and nav on two tight
   * lines, then a 1180 px page. Two headers stacked is what made the operator's
   * screens look nothing like it. So `routes/admin.tsx` owns all of it, and this
   * file steps aside on one condition rather than trying to be both.
   *
   * `4xl` below is a READING width, sized for what the PERSON sees - a
   * conversation, one column of prose - and it is what remains here.
   */
  if (pathname.startsWith('/admin')) return <Outlet />;

  /**
   * The SESSION paints its own window too, and for the opposite reason (§5.3,
   * issue #60).
   *
   * « `/s/...` prend toute la fenêtre et est peinte par le thème de
   * l'expérience : rien de PMS n'y subsiste, sinon le thème ne servirait à
   * rien. » The logo above and the 896 px reading column below are exactly the
   * two things that must not survive there - the first is another brand on
   * somebody's experience, the second would leave the theme painting a strip
   * down the middle of a white page.
   *
   * `/l/<secret>` stays here on purpose, and the spec spells that difference
   * out: the door « est encore une porte d'accueil, et elle ne doit rien
   * promettre du visuel de l'expérience ». Two coquilles, not one.
   */
  if (pathname.startsWith('/s/')) return <Outlet />;

  /**
   * The gap above the first line of a page, and why it is this small (issue #21).
   *
   * `py-4` here plus `pt-8` on the `main` below used to stack to 48 px of
   * nothing between the wordmark and the first word - and the screens under
   * this shell add their own top padding on top of that, which put the chat
   * lobby's « Je suis prêt » under the fold of a laptop. `py-3` + `pt-4`
   * halves it, and the protection zone the charte asks for around the logo -
   * the height of the wordmark's « p » - still fits inside `py-3` at this
   * logo height (32 px painted, so the « p » is a few pixels).
   *
   * This is the shell of the PUBLIC screens that keep the PMS chrome: `/`,
   * `/chat/<jeton>` and `/l/<secret>` move together, which is deliberate - the
   * gap was the same mistake on all of them. `/admin` and `/s/<jeton>` paint
   * their own windows and are untouched (the two `return`s above).
   */
  return (
    <div className="mx-auto flex min-h-screen max-w-4xl flex-col px-6">
      <header className="flex items-center gap-4 py-3">
        {/*
          Colour logo on white - the default pairing of the charte (§02).
          Minimum legible width is 120px; the protection zone around it is the
          height of the wordmark's "p", which the header padding provides.
        */}
        <Link to="/" className="no-underline">
          <img
            src="/brand/logo-horizontal-transparent.png"
            alt="patrickmathieu singularité"
            width={160}
            height={32}
            className="h-8 w-auto"
          />
        </Link>
      </header>
      {/* No bottom padding: the session composer anchors itself to the
          viewport bottom and owns its own breathing room. */}
      <main className="flex flex-1 flex-col pt-4">
        <Outlet />
      </main>
    </div>
  );
}
